import { CommonModule, ViewportScroller } from '@angular/common'
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core'
import { ConfigService } from '../../../../src/app/services/config.service'
import { MessagehandlerService } from '../../../../src/app/services/messagehandler.service'
import { Statistics, ApplicationEvent, ToastMessage, User } from '../../../../src/app/types'
import { ToastContainerDirective, ToastrService } from 'ngx-toastr'
import { ResearchService } from '../../../../src/app/services/research.service'
import { NavigationComponent } from '../../../../src/app/components/navigation/navigation.component'
import { Router, RouterOutlet } from '@angular/router'
import { jwtDecode } from 'jwt-decode'
import { UserService } from '../../../../src/app/services/user.service'
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco'
import { StatisticsService } from '../../../../src/app/services/statistics.service'
import { BehaviorSubject, take } from 'rxjs'

@Component({
   selector: 'app-root',
   standalone: true,
   imports: [CommonModule, NavigationComponent, RouterOutlet, ToastContainerDirective, TranslocoDirective],
   templateUrl: './app.component.html',
   encapsulation: ViewEncapsulation.ShadowDom
})
export class AppComponent implements OnInit, OnDestroy, OnChanges {

   // NOTE for booleans use strings, since boolean type doesn't work in web component attributes
   @Input() height = '100%'
   @Input() libraryName = 'Library'
   @Input() clientKey = ''
   @Input() product = 'primo'
   @Input() language = 'en'
   @Input() apiUrl = '/cda'
   @Input() showsToasts = 'true'
   @Input() sourceUrl = import.meta.url
   @Input() token = ''
   @Input() showSideNav = ''
   @Input() apiParams = ''
   @Input() showBeta = 'true'
   @Input() apiJwt = ''
   @Input() query = ''
   @Input() debug = 'false'
   @Input() streaming = 'false'
   @Input() showAdvanced = 'false'
   @Input() enableHistory = 'true'
   @Input() saveAvailable = 'true'
   @Input() howToCiteLink = ''
   @Input() researchHistoryDays = this.config.researchHistoryDays.toString()

   // for notificationmessages (aka toasts)
   @Output() toast = new EventEmitter<ToastMessage>()

   // for statistics in external source
   @Output() stats = new EventEmitter<Statistics>()

   // to listen to the application being ready etc, or throw an error so you can do something with that (like launch help chat)
   @Output() application = new EventEmitter<ApplicationEvent>()

   // this for our toast UI
   @ViewChild(ToastContainerDirective, { static: true }) toastContainer!: ToastContainerDirective

   elements: HTMLElement[] = []
   initialQuery = ''
   translationLoaded = new BehaviorSubject<boolean>(false)

   get showSideNavCurrent(): boolean | null {
      return this.config.showSideNav
   }

   constructor(
      private config: ConfigService,
      private research: ResearchService,
      private message: MessagehandlerService,
      private toastr: ToastrService,
      private renderer2: Renderer2,
      private elRef: ElementRef,
      private router: Router,
      private viewport: ViewportScroller,
      private transloco: TranslocoService,
      private statsService: StatisticsService,
      private userService: UserService) {
      // navigate to the root by default - kind of a workaround since we're using a MockLocationStrategy in the web component...
      // this is needed otherwise the landing page does not show
      this.router.navigateByUrl('/')
      statsService.onEvent()
         .subscribe((stats) => {
            this.stats.emit(stats)
         })

      this.transloco.selectTranslate('research.status.answering')
         .pipe(take(1))
         .subscribe(() => {
            this.translationLoaded.next(true)
         })

      message.appEvents$.subscribe((statusEvent) => {
         this.application.emit(statusEvent)
      })
      message.emitAppEvent({ event:'application', status: 'initializing'})

   }

   @HostListener('document:visibilitychange', ['$event'])
   visibilityChangeHandler() {
      // this is the event that often indicates the tab is not used, so stop all events and
      // TODO send statistics?
      if (this.elRef.nativeElement.ownerDocument.hidden) {
         this.research.close()
      }
   }

   skipTo(id: string) {
      this.viewport.scrollToAnchor(id)
   }

   ngOnChanges() {
      this.loadConfigFromInputs()
   }

   ngOnInit() {
      this.loadConfigFromInputs()

      // set the height as a CSS var for usage later
      this.elRef.nativeElement.ownerDocument?.documentElement?.style?.setProperty('--ra-height', this.height)
      // this for our toast UI
      this.toastr.overlayContainer = this.toastContainer

      // this way the includer can capture toasts and show them in their own toast service instead :)
      this.message.onToast$.subscribe((message) => {
         this.toast.emit(message)
      })

      const fontUrl = new URL('https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@200..900&display=swap', this.sourceUrl)
      this.addStylesheetElement(fontUrl, this.elRef.nativeElement.getRootNode().getElementsByTagName('head')[0])

      // insert styles
      const cssUrl = new URL('cdra-styles.css', this.sourceUrl)
      this.addStylesheetElement(cssUrl)

      if (this.showsToasts) {
         const cssToastsUrl = new URL('cdra-toastr.css', this.sourceUrl)
         this.addStylesheetElement(cssToastsUrl)
      }

      // set dynamic URL for font loading
      const el = this.renderer2.createElement('style') as HTMLLinkElement
      const fontFace = this.renderer2.createText(`@font-face {
         font-family: 'RegularClarivate-Bold';
         src: url('${new URL('/assets/fonts/RegularClarivate-Bold.woff2', this.sourceUrl)}') format('woff2'), url('${new URL('assets/fonts/RegularClarivate-Bold.woff', this.sourceUrl)}') format('woff');
         font-display: swap;
      }`)
      el.appendChild(fontFace)
      this.renderer2.appendChild(this.elRef.nativeElement.getRootNode().getElementsByTagName('head')[0], el)
      this.elements.push(el)
      this.message.emitAppEvent({ event: 'application', status: 'ready'})
   }

   loadConfigFromInputs() {
      const apiParams = JSON.parse(this.apiParams)
      // do not do the query before translations are loaded
      this.translationLoaded.asObservable()
         .pipe().subscribe((loaded) => {
            if(loaded) {
               if(this.query !== this.initialQuery) {
                  this.initialQuery = this.query
                  this.statsService.search(this.query, 'webcomponent')
                  this.research.getAnswer(this.query, null).then(() => {
                  // now do not show the query anymore (can happen on config reload)
                     this.query = ''
                  })
               }
            }
         })

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let decodedToken: any = {}
      if (this.apiJwt) {
         const jwt = sessionStorage.getItem(this.apiJwt)
         if (jwt) {
            try {
               decodedToken = jwtDecode(jwt)
            }
            catch (e) {
               console.error('Issue decoding token from sessionStorage', e)
            }
         }
      }
      const user: User = {
         id: decodedToken.user ?? apiParams?.user ?? 'session:ui',
         userId: decodedToken.user ?? apiParams?.user ?? 'session:ui',
         clientKey: this.clientKey,
         remaining: -1,
         maxRemaining: -1
      }
      this.userService.setUser(user)

      if (this.debug && this.debug !== 'false') {
         this.config.setDebug(true)
         this.config.setFeatureFromString(this.debug)
      }

      // initialize with the configuration from the web component attributes, we can maybe augment config with info from API later on?
      this.config.setConfig({
         isStandalone: false,
         showToast: this.showsToasts === 'true',
         height: this.height,
         libraryName: this.libraryName,
         product: this.product,
         language: this.language,
         apiUrl: this.apiUrl,
         showSideNav: null,
         showBeta: this.showBeta === 'true',
         apiParams: apiParams ?? {},
         apiJwt: this.apiJwt,
         sourceUrl: this.sourceUrl,
         streamAnswer: this.streaming === 'true',
         showAdvanced: this.showAdvanced === 'true',
         enableHistory: this.enableHistory === 'true',
         howToCiteLink: this.howToCiteLink,
         maxHistorySize: this.config.maxHistorySizeDefault,
         researchHistoryDays: this.researchHistoryDays ? parseInt(this.researchHistoryDays) : this.config.researchHistoryDays
      })
   }

   ngOnDestroy() {
      for(const element of this.elements) {
         this.renderer2.removeChild(this.elRef.nativeElement.shadowRoot, element)
      }
   }

   private addStylesheetElement(url: URL, attachTo = this.elRef.nativeElement.shadowRoot) {
      const el = this.renderer2.createElement('link') as HTMLLinkElement
      this.renderer2.setProperty(el, 'rel', 'stylesheet')
      this.renderer2.setProperty(el, 'href', url)

      this.renderer2.appendChild(attachTo, el)
      this.elements.push(el)
   }
}
